import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Group,
  MediaQuery,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  SegmentedControl,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useLiveQuery } from "dexie-react-hooks";
import { nanoid } from "nanoid";
import { KeyboardEvent, useState, type ChangeEvent, useEffect } from "react";
import { AiOutlineSend } from "react-icons/ai";
import { MessageItem } from "../components/MessageItem";
import { ModelSelector, WritingOptionsSelector, WritingOptions, ProviderBadge } from "../components/dynamic";
import { db } from "../db";
import { useChatId } from "../hooks/useChatId";
import { config } from "../utils/config";
import { ChatCompletionRequestMessage } from "openai";
import {
  createChatCompletion,
  createStreamChatCompletion,
  providerRegistry
} from "../utils/providers";

export function ChatRoute() {
  const chatId = useChatId();
  const settings = useLiveQuery(async () => {
    return db.settings.where({ id: "general" }).first();
  });
  
  // Get the API key for the current provider
  const apiKey = settings?.provider && settings?.providerSettings?.[settings.provider]?.apiKey;
  const messages = useLiveQuery(() => {
    if (!chatId) return [];
    return db.messages.where("chatId").equals(chatId).sortBy("createdAt");
  }, [chatId]);
  const userMessages =
    messages
      ?.filter((message) => message.role === "user")
      .map((message) => message.content) || [];
  const [userMsgIndex, setUserMsgIndex] = useState(0);
  const [content, setContent] = useState("");
  const [contentDraft, setContentDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const chat = useLiveQuery(async () => {
    if (!chatId) return null;
    return db.chats.get(chatId);
  }, [chatId]);

  const [writingOptions, setWritingOptions] = useState<WritingOptions>({
    character: null,
    tone: null,
    style: null,
    format: null
  });

  const getSystemMessage = () => {
    const message: string[] = [];
    if (writingOptions.character) message.push(`You are ${writingOptions.character}.`);
    if (writingOptions.tone) message.push(`Respond in ${writingOptions.tone} tone.`);
    if (writingOptions.style) message.push(`Respond in ${writingOptions.style} style.`);
    if (writingOptions.format) message.push(writingOptions.format);
    if (message.length === 0)
      message.push(
        "You are ChatGPT, a large language model trained by OpenAI."
      );
    return message.join(" ");
  };

  const handleWritingOptionsChange = (options: WritingOptions) => {
    setWritingOptions(options);
  };

  const [model, setModel] = useState(config.defaultModel);
  useEffect(() => {
    if (settings?.model) {
      setModel(settings.model);
    }
  }, [settings]);

  const submit = async () => {
    if (submitting) return;

    if (!chatId) {
      notifications.show({
        title: "Error",
        color: "red",
        message: "chatId is not defined. Please create a chat to get started.",
      });
      return;
    }

    if (!apiKey) {
      // Get the provider name for a more specific error message
      const providerName = settings?.provider 
        ? providerRegistry.get(settings.provider)?.name || "AI provider" 
        : "AI provider";
        
      notifications.show({
        title: "Error",
        color: "red",
        message: `${providerName} API Key is not defined. Please set your API Key in settings.`,
      });
      return;
    }

    try {
      setSubmitting(true);

      await db.messages.add({
        id: nanoid(),
        chatId,
        content,
        role: "user",
        createdAt: new Date(),
      });
      setContent("");

      const messageId = nanoid();
      await db.messages.add({
        id: messageId,
        chatId,
        content: "█",
        role: "assistant",
        createdAt: new Date(),
      });

      const messageArray: ChatCompletionRequestMessage[] = [
        {
          role: "system",
          content: getSystemMessage(),
        },
        ...(messages ?? []).map((message) => ({
          role: message.role,
          content: message.content,
        })),
        { role: "user", content },
      ];
      
      await createStreamChatCompletion(
        messageArray,
        chatId,
        messageId
      );

      setSubmitting(false);

      if (chat?.description === "New Chat") {
        try {
          const messages = await db.messages
            .where({ chatId })
            .sortBy("createdAt");
          const titleMessageArray: ChatCompletionRequestMessage[] = [
            {
              role: "system",
              content: getSystemMessage(),
            },
            ...(messages ?? []).map((message) => ({
              role: message.role,
              content: message.content,
            })),
            {
              role: "user",
              content:
                "What would be a short and relevant title for this chat? You must strictly answer with only the title, no other text is allowed.",
            },
          ];
          
          // Generate chat title
          const createChatDescription = await createChatCompletion(titleMessageArray);
          
          // Check if the response has the expected structure
          let chatDescription = "New Chat";
          let totalTokens = 0;
          
          if (createChatDescription?.data?.choices?.[0]?.message?.content) {
            chatDescription = createChatDescription.data.choices[0].message.content;
          }
          
          if (createChatDescription?.data?.usage?.total_tokens) {
            totalTokens = createChatDescription.data.usage.total_tokens;
          }
          
          // Update the chat description and token count
          await db.chats.where({ id: chatId }).modify((chat) => {
            chat.description = chatDescription;
            if (chat.totalTokens) {
              chat.totalTokens += totalTokens;
            } else {
              chat.totalTokens = totalTokens;
            }
          });
        } catch (error) {
          console.error("Failed to generate chat title:", error);
          // Update with default title if title generation fails
          await db.chats.where({ id: chatId }).modify((chat) => {
            chat.description = "New Chat";
          });
        }
      }
    } catch (error: any) {
      // Handle network errors
      if (error.name === "AxiosError" && error.message === "Network Error") {
        notifications.show({
          title: "Error",
          color: "red",
          message: "No internet connection.",
        });
      } else {
        // Handle API errors
        const message = error.response?.data?.error?.message || error.message || "An unknown error occurred";
        notifications.show({
          title: "Error",
          color: "red",
          message,
        });
      }
      console.error("Chat submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const onUserMsgToggle = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    if (
      !["ArrowUp", "ArrowDown"].includes(event.code) ||
      selectionStart !== selectionEnd ||
      (event.code === "ArrowUp" && selectionStart !== 0) ||
      (event.code === "ArrowDown" &&
        selectionStart !== event.currentTarget.value.length)
    ) {
      // do nothing
      return;
    }
    event.preventDefault();

    const newMsgIndex = userMsgIndex + (event.code === "ArrowUp" ? 1 : -1);
    const allMessages = [contentDraft, ...Array.from(userMessages).reverse()];

    if (newMsgIndex < 0 || newMsgIndex >= allMessages.length) {
      // index out of range, do nothing
      return;
    }
    setContent(allMessages.at(newMsgIndex) || "");
    setUserMsgIndex(newMsgIndex);
  };

  const onContentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    setContent(value);
    setContentDraft(value);
    setUserMsgIndex(0);
  };

  if (!chatId) return null;

  return (
    <>
      <Container pt="xl" pb={100} sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 100 }}>
          <ProviderBadge />
        </Box>
        <Stack spacing="xs">
          {messages?.map((message) => (
            <MessageItem key={message.id} message={message} />
          ))}
        </Stack>
        {submitting && (
          <Card withBorder mt="xs">
            <Skeleton height={8} radius="xl" />
            <Skeleton height={8} mt={6} radius="xl" />
            <Skeleton height={8} mt={6} radius="xl" />
            <Skeleton height={8} mt={6} radius="xl" />
            <Skeleton height={8} mt={6} width="70%" radius="xl" />
          </Card>
        )}
      </Container>
      <Box
        py="lg"
        sx={(theme) => ({
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          [`@media (min-width: ${theme.breakpoints.md})`]: {
            left: 300,
          },
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[0],
        })}
      >
        {messages?.length === 0 && <ModelSelector disabled={submitting} />}
        <Container>
          {messages?.length === 0 && (
            <WritingOptionsSelector 
              disabled={submitting} 
              onChange={handleWritingOptionsChange} 
            />
          )}
          <Flex gap="sm">
            <Textarea
              key={chatId}
              sx={{ flex: 1 }}
              placeholder="Your message here..."
              autosize
              autoFocus
              disabled={submitting}
              minRows={1}
              maxRows={5}
              value={content}
              onChange={onContentChange}
              onKeyDown={async (event) => {
                if (event.code === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                  setUserMsgIndex(0);
                }
                if (event.code === "ArrowUp") {
                  onUserMsgToggle(event);
                }
                if (event.code === "ArrowDown") {
                  onUserMsgToggle(event);
                }
              }}
            />
            <MediaQuery largerThan="sm" styles={{ display: "none" }}>
              <Button
                h="auto"
                onClick={() => {
                  submit();
                }}
              >
                <AiOutlineSend />
              </Button>
            </MediaQuery>
          </Flex>
        </Container>
      </Box>
    </>
  );
}
